import type { Profile, User, UserMainRole } from '~/types';
import api from '~/util/api';

export type UserAccessResponse = {
  hasFullAccess: boolean;
  onlyQuizAccess?: boolean;
  onlyDashboardAccess?: boolean;
};

export interface ListUsersParams {
  page?: number;
  pageSize?: number;
  filter?: string;
  role?: string;
  sortBy?: 'name' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}

interface ListUsersApiResponse {
  data: User[];
  total_items: number;
  page: number;
  page_size: number;
}

export interface ListUsersResponse {
  data: User[];
  page: number;
  total_items: number;
  total_pages: number;
}

interface GetUserApiResponse {
  data: User;
}

export interface UserCourseApi {
  id: string;
  title: string;
  description: string;
  price: number;
  thumbnail_url: string | null;
  category_id: string | null;
  course_type_id: string;
  video_course_id: string;
  created_by_id: string;
  teacher_id: string | null;
  status: string;
  discount: number;
  access_months: number;
  created_at: string;
  updated_at: string;
}

/** Objeto da compra em cada item do GET /user/:id/courses (uso de purchase.id em PATCH /purchase/:id/cancel) */
export interface UserPurchaseApi {
  id: string;
  user_id: string;
  course_id: string;
  coupon_id: string | null;
  price: number;
  payment_status: string;
  external_id: string | null;
  payment_link_url: string | null;
  original_expires_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  is_manually_unlocked?: boolean;
  manual_unlock_comments?: string | null;
  refund_amount_cents?: number;
  cancel_comments?: string | null;
}

/** Cada item do array `data` do GET /user/:id/courses */
export interface UserCoursePurchaseItemApi {
  course: UserCourseApi;
  coupon_code: string | null;
  purchase: UserPurchaseApi;
}

/** Body obrigatório para PUT /user/:id (atualização de dados do usuário). */
export interface UpdateUserPayload {
  first_name: string;
  last_name: string;
  phone: string;
}

const UserService = {
  getAccess: async (): Promise<UserAccessResponse> => {
    const response = await api.get<UserAccessResponse>('/user/access');
    return response.data;
  },

  getUser: async (userId: string): Promise<User> => {
    const response = await api.get<GetUserApiResponse>(`/user/${userId}`);
    return response.data.data;
  },

  getUsers: async (params: ListUsersParams = {}): Promise<ListUsersResponse> => {
    const { page = 0, pageSize = 10, filter, role, sortBy, sortOrder } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (filter) queryParams.append('filter', filter);
    if (role) queryParams.append('role', role);
    if (sortBy) queryParams.append('sort_by', sortBy);
    if (sortOrder) queryParams.append('sort_order', sortOrder);

    const response = await api.get<ListUsersApiResponse>(`/user?${queryParams.toString()}`);
    const { data, total_items, page: returnedPage, page_size } = response.data;

    return {
      data,
      page: returnedPage,
      total_items,
      total_pages: Math.ceil(total_items / page_size),
    };
  },

  getTeachers: async (params: ListUsersParams = {}) => {
    const { page = 1, pageSize = 10, filter } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (filter) {
      queryParams.append('filter', filter);
    }

    const response = await api.get<ListUsersResponse>(`/teachers?${queryParams.toString()}`);
    return response.data;
  },

  getProfiles: async () => {
    const response = await api.get<{ data: Profile[]; total_items: number }>(`/user/group`);
    return response.data;
  },

  updateUser: async (userId: string, payload: UpdateUserPayload): Promise<void> => {
    await api.put(`/user/${userId}`, payload);
  },

  updateUserRole: async (userId: string, role: UserMainRole): Promise<void> => {
    await api.patch(`/user/${userId}/role`, { role });
  },

  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`/user/${userId}`);
  },

  sendPasswordReset: async (userId: string): Promise<void> => {
    await api.post(`/user/${userId}/send-password-reset`);
  },

  sendEmailVerification: async (userId: string): Promise<void> => {
    await api.post(`/user/${userId}/send-email-verification`);
  },

  getUserCourses: async (userId: string): Promise<UserCoursePurchaseItemApi[]> => {
    const response = await api.get<{ data: UserCoursePurchaseItemApi[] }>(
      `/user/${userId}/courses`,
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data : [];
  },
};

export default UserService;
